import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { post, C, LOGO_BASE, THUMB_BASE } from "./utils";
import { ThemeCtx, Icon } from "./AppShell";
import ReactDOM from "react-dom";

const EVENTS_TYPES = "94";

const isImage   = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName  = (p="") => p.split("/").pop();
const stripHtml = (h="") => h ? h.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim() : "";
const getPrimaryProduct    = (a=[]) => a.find(p=>(p.PRIMARY==="1"||p.PRIMARY===1)&&String(p.BRANDID)!=="0")?.BRANDNAME||"";
const getPrimaryIndication = (a=[]) => a.find(i=>(i.PRIMARY==="1"||i.PRIMARY===1)&&String(i.INDICATIONID)!=="0")?.INDICATIONNAME||"";

// ─── Phase detection ──────────────────────────
const openDocTab = (docId, subId) => {
  if (!docId) return;
  const base = window.location.origin + window.location.pathname;
  window.open(`${base}?doc=${docId}&dt=94&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};

const PHASE_CFG = {
  "Approved":  { bar:"#059669", light:"#ECFDF5", text:"#065F46", grad:"linear-gradient(135deg,#059669,#34D399)" },
  "Phase III": { bar:"#1D4ED8", light:"#EFF6FF", text:"#1E3A8A", grad:"linear-gradient(135deg,#1D4ED8,#60A5FA)" },
  "Phase II":  { bar:"#7C3AED", light:"#F5F3FF", text:"#4C1D95", grad:"linear-gradient(135deg,#7C3AED,#C084FC)" },
  "Phase I":   { bar:"#0891B2", light:"#ECFEFF", text:"#164E63", grad:"linear-gradient(135deg,#0891B2,#67E8F9)" },
  "Other":     { bar:"#6B7280", light:"#F9FAFB", text:"#374151", grad:"linear-gradient(135deg,#6B7280,#D1D5DB)" },
};
const PHASE_ORDER = ["Approved","Phase III","Phase II","Phase I","Other"];

function normalizePhase(raw) {
  if (!raw) return null;
  const t = raw.toString().toUpperCase().trim();
  if (t.includes("APPROV"))          return "Approved";
  if (t.includes("III")||t.includes("3")) return "Phase III";
  if (t.includes("II")||t.includes("2"))  return "Phase II";
  if (t.includes("I")||t.includes("1"))   return "Phase I";
  return null;
}
function detectPhase(doc) {
  // Check exact "PHASE" key from API first
  if (doc.PHASE) {
    const norm = normalizePhase(doc.PHASE);
    if (norm) return norm;
  }
  // Fall back to text scanning
  const t = `${doc.SUBJECT||""} ${doc.NOTES||""} ${doc.ACRONYMS||""}`.toUpperCase();
  if (t.includes("APPROV"))                            return "Approved";
  if (t.includes("PHASE III")||t.includes("PHASE 3")) return "Phase III";
  if (t.includes("PHASE II")||t.includes("PHASE 2"))  return "Phase II";
  if (t.includes("PHASE I")||t.includes("PHASE 1"))   return "Phase I";
  return "Other";
}

// ─── Flag SVG ─────────────────────────────────
function FlagIcon({ country, size=14 }) {
  const c=(country||"").trim();
  if(c==="US"||c==="USA"||c==="United States") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ev_us"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ev_us)">
        <rect width="32" height="32" fill="#B22234"/>
        {[0,1,2,3,4,5].map(i=><rect key={i} y={i*4.9+2.45} width="32" height="2.45" fill="#fff"/>)}
        <rect width="13" height="15" fill="#3C3B6E"/>
        {[[2,2],[5,2],[8,2],[11,2],[3.5,4.5],[6.5,4.5],[9.5,4.5],[2,7],[5,7],[8,7],[11,7],[3.5,9.5],[6.5,9.5],[9.5,9.5],[2,12],[5,12],[8,12],[11,12]].map(([x,y],i)=>(
          <circle key={i} cx={x} cy={y} r="0.9" fill="#fff"/>
        ))}
      </g>
    </svg>
  );
  if(c==="EU"||c==="Europe"||c==="European Union") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ev_eu"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ev_eu)">
        <rect width="32" height="32" fill="#003399"/>
        {Array.from({length:12},(_,i)=>{const a=(i/12)*2*Math.PI-Math.PI/2;return <text key={i} x={16+10*Math.cos(a)-3} y={16+10*Math.sin(a)+4} fontSize="6" fill="#FFDD00">★</text>;})}
      </g>
    </svg>
  );
  if(c==="Global"||c==="International"||c==="Worldwide") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <circle cx="16" cy="16" r="16" fill="#E0F2FE"/>
      <ellipse cx="16" cy="16" rx="7" ry="16" fill="none" stroke="#0891B2" strokeWidth="1"/>
      <line x1="0" y1="16" x2="32" y2="16" stroke="#0891B2" strokeWidth="1"/>
    </svg>
  );
  if(c==="UK"||c==="United Kingdom") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ev_uk"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ev_uk)">
        <rect width="32" height="32" fill="#012169"/>
        <path d="M0,0 L32,32 M32,0 L0,32" stroke="#fff" strokeWidth="6"/>
        <path d="M0,0 L32,32 M32,0 L0,32" stroke="#C8102E" strokeWidth="3"/>
        <path d="M16,0 L16,32 M0,16 L32,16" stroke="#fff" strokeWidth="8"/>
        <path d="M16,0 L16,32 M0,16 L32,16" stroke="#C8102E" strokeWidth="5"/>
      </g>
    </svg>
  );
  if(c==="Japan"||c==="JP") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <clipPath id="ev_jp"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ev_jp)"><rect width="32" height="32" fill="#fff"/><circle cx="16" cy="16" r="8" fill="#BC002D"/></g>
    </svg>
  );
  if(!c) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{borderRadius:"50%",flexShrink:0}}>
      <circle cx="16" cy="16" r="16" fill="#64748B"/>
      <text x="16" y="21" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff">{c.slice(0,2).toUpperCase()}</text>
    </svg>
  );
}

// ─── Parse event datetime "05 Feb, 26 7:00 AM CST" ───
function parseEventDate(raw) {
  if (!raw) return null;
  const MON={Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
  // "05 Feb, 26 7:00 AM CST" or "05 Feb, 2026 7:00 AM"
  let m = raw.match(/(\d{1,2})\s+([A-Za-z]+)[,\s]+(\d{2,4})/);
  if (m) {
    const yr = m[3].length===2 ? 2000+parseInt(m[3]) : parseInt(m[3]);
    return new Date(yr, MON[m[2]]??0, parseInt(m[1]));
  }
  // "YYYY-MM-DD"
  m = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1],+m[2]-1,+m[3]);
  return null;
}

const fmtDate     = d => d ? d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "";
const fmtDateShort= d => d ? d.toLocaleDateString("en-GB",{day:"2-digit",month:"short"}) : "";
const fmtTime     = raw => {
  if (!raw) return "";
  const m = raw.match(/(\d{1,2}:\d{2}\s*(?:AM|PM)?(?:\s*\w+)?)/i);
  return m ? m[1].trim() : "";
};

// ─── Data loader ──────────────────────────────
function useEventsLoader({ user, subproject, eventtype }) {
  const [docs,setDocs]=useState([]), [loading,setLoading]=useState(false), [error,setError]=useState(null);
  const load = useCallback(async () => {
    if (!subproject) return;
    setLoading(true); setError(null);
    try {
      const r = await post("advance_filter",{
        subprojectid:subproject.SUBPROJECTID, userid:user.USERID,
        doctypeid:EVENTS_TYPES, stateid:2,
        sort:"latest", eventtype,
      }, user.AUTH_CODE);
      setDocs(r.STATUS==="1"&&Array.isArray(r.DATA)?r.DATA:[]);
    } catch { setError("Failed to load events."); }
    setLoading(false);
  },[subproject,user,eventtype]);
  useEffect(()=>{ load(); },[subproject?.SUBPROJECTID, eventtype]);
  return { docs, loading, error };
}

function buildEntries(docs) {
  return docs.map(doc => {
    const companies   = Array.isArray(doc.COMPANY) ? doc.COMPANY : [];
    const company     = companies.find(c=>c.PRIMARY===1||c.PRIMARY==="1")||companies[0]||null;
    const products    = Array.isArray(doc.PRODUCTS)    ? doc.PRODUCTS    : [];
    const indications = Array.isArray(doc.INDICATIONS) ? doc.INDICATIONS : [];
    const chatbot     = Array.isArray(doc.CHATBOT)     ? doc.CHATBOT     : [];
    const imgFiles    = chatbot.filter(a=>isImage(a.FILEPATH));

    // Event date — try EVENTDATE, EVENTNEWS, DISPLAY_TIME
    const rawDate     = doc.EVENTDATE||doc.EVENTNEWS||doc.EVENT_DATE||doc.DISPLAY_TIME||"";
    const eventDate   = parseEventDate(rawDate);
    const timeStr     = fmtTime(rawDate);

    const logoUrl     = company?.COMPANY_LOGO ? LOGO_BASE+company.COMPANY_LOGO : null;
    const sponsor     = doc.SPONSORNAME || company?.SPONSORNAME || "";
    const allSponsors = [...new Set([doc.SPONSORNAME,...companies.map(c=>c.SPONSORNAME)].filter(Boolean))];
    const countries   = [...new Set([doc.COUNTRYNAME||"",...companies.map(c=>c.COUNTRYNAME||"")].filter(Boolean))];

    return {
      id:          doc.DOCUMENTID,
      subject:     doc.SUBJECT||"Untitled",
      notes:       stripHtml(doc.NOTES||""),
      acronym:     typeof doc.ACRONYMS==="string" ? doc.ACRONYMS : "",
      phase:       detectPhase(doc),
      rawPhase:    doc.PHASE||doc.PHASENAME||"",
      sponsor,     allSponsors,
      logoUrl,
      product:     getPrimaryProduct(products),
      indication:  getPrimaryIndication(indications),
      thumbUrl:    imgFiles.length>0 ? THUMB_BASE+fileName(imgFiles[0].FILEPATH) : null,
      eventDate,
      rawDate,
      timeStr,
      displayTime: doc.DISPLAY_TIME||"",
      countries,
      location:    doc.LOCATION||"",
      docType:     doc.DOCTYPENAME||"",
      count:       doc.COUNT||0, likes:doc.LIKES||0, comments:doc.COMMENTCOUNT||0,
      fav:         doc.MYFAVOURITES==="YES",
    };
  });
}


// ─── Drag-to-scroll hook ──────────────────────
function useDragScroll() {
  const ref = useRef(null);
  const drag = useRef({ active:false, startX:0, scrollX:0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onDown = e => {
      drag.current = { active:true, startX:e.clientX, scrollX:el.scrollLeft };
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };
    const onMove = e => {
      if (!drag.current.active) return;
      el.scrollLeft = drag.current.scrollX - (e.clientX - drag.current.startX);
    };
    const onUp = () => {
      if (!drag.current.active) return;
      drag.current.active = false;
      el.style.cursor = "grab";
      el.style.userSelect = "";
    };

    el.addEventListener("mousedown", onDown);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseup", onUp);
    el.addEventListener("mouseleave", onUp);

    return () => {
      el.removeEventListener("mousedown", onDown);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseup", onUp);
      el.removeEventListener("mouseleave", onUp);
    };
  });  // no deps — re-attaches if ref changes

  return ref;
}

function ScrollChips({ label, items, active, onSelect, activeColor, activeBg, renderLabel }) {
  const ref = useDragScroll();
  if (!items.length) return null;
  return (
    <div style={{display:"flex",alignItems:"center",gap:"6px",minWidth:0}}>
      <span style={{fontSize:"10px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",
        letterSpacing:"0.5px",flexShrink:0,minWidth:"56px"}}>{label}</span>
      <div ref={ref} style={{flex:1,minWidth:0,overflowX:"auto",overflowY:"hidden",
        scrollbarWidth:"none",msOverflowStyle:"none",cursor:"grab",
        WebkitOverflowScrolling:"touch",userSelect:"none"}}>
        <div style={{display:"flex",gap:"6px",width:"max-content",padding:"2px 0"}}>
          {items.map(item=>{
            const a = active===item;
            return (
              <button key={item}
                onMouseDown={e=>e.stopPropagation()}
                onClick={()=>onSelect(a?null:item)}
                style={{padding:"3px 10px",borderRadius:"20px",whiteSpace:"nowrap",flexShrink:0,
                  border:`1.5px solid ${a?activeColor:"#E5E7EB"}`,
                  background:a?activeBg:"#fff",color:a?activeColor:"#6B7280",
                  fontSize:"11px",fontWeight:a?"700":"500",
                  cursor:"pointer",transition:"all 0.15s"}}>
                {renderLabel(item)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─── Smart tooltip position ──────────────────
function smartPos(x, y, tipW=340, tipH=220) {
  const vw = window.innerWidth, vh = window.innerHeight;
  let left = x + 18, top = y - 14;
  if (left + tipW > vw - 8) left = x - tipW - 10;
  if (left < 8)              left = 8;
  if (top + tipH > vh - 8)  top  = vh - tipH - 8;
  if (top < 8)               top  = 8;
  return { left, top };
}

// ─── Tooltip ──────────────────────────────────
function Tooltip({ entry, x, y }) {
  const ph = PHASE_CFG[entry.phase]||PHASE_CFG["Other"];
  const { left, top } = smartPos(x, y);
  return ReactDOM.createPortal(
    <div style={{position:"fixed",left,top,zIndex:99999,pointerEvents:"none",
      background:"#0F172A",color:"#fff",borderRadius:"14px",padding:"14px 16px",
      minWidth:"270px",maxWidth:"360px",fontSize:"12px",lineHeight:"1.6",
      boxShadow:`0 20px 60px rgba(0,0,0,0.5),0 0 0 1px ${ph.bar}55`}}>
      <div style={{height:"3px",background:ph.grad,borderRadius:"2px",marginBottom:"10px"}}/>
      <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"8px"}}>
        {entry.logoUrl
          ? <img src={entry.logoUrl} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"28px",height:"28px",objectFit:"contain",borderRadius:"6px",background:"#fff",padding:"2px"}}/>
          : <div style={{width:"28px",height:"28px",borderRadius:"6px",background:ph.bar,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <span style={{fontSize:"13px",fontWeight:"800",color:"#fff"}}>{entry.sponsor?.[0]||"?"}</span>
            </div>}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:"800",fontSize:"13px",color:"#F1F5F9",lineHeight:1.2}}>
            {entry.product||entry.subject.split(" ").slice(0,5).join(" ")}
          </div>
          {entry.sponsor&&<div style={{fontSize:"11px",color:"#64748B"}}>{entry.sponsor}</div>}
        </div>
        <span style={{fontSize:"10px",fontWeight:"700",color:ph.bar,background:`${ph.bar}22`,
          borderRadius:"4px",padding:"2px 8px",border:`1px solid ${ph.bar}44`,flexShrink:0}}>
          {entry.phase==="Other"&&entry.rawPhase?entry.rawPhase:entry.phase}
        </span>
      </div>
      {entry.acronym&&<div style={{color:"#FCD34D",fontWeight:"800",fontSize:"13px",marginBottom:"3px"}}>{entry.acronym}</div>}
      {entry.indication&&<div style={{color:"#A5B4FC",fontWeight:"600",marginBottom:"4px"}}>{entry.indication}</div>}
      {entry.subject&&<div style={{color:"#94A3B8",fontSize:"11px",lineHeight:"1.5",marginBottom:"6px",
        borderLeft:`2px solid ${ph.bar}55`,paddingLeft:"8px"}}>
        {entry.subject.length>120?entry.subject.slice(0,120)+"…":entry.subject}</div>}
      {(entry.countries.length>0||entry.location)&&(
        <div style={{display:"flex",alignItems:"center",gap:"6px",marginBottom:"5px",flexWrap:"wrap"}}>
          {entry.countries.map((c,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:"3px"}}>
              <FlagIcon country={c} size={13}/>
              <span style={{fontSize:"10px",color:"#94A3B8"}}>{c}</span>
            </div>
          ))}
          {entry.location&&!entry.countries.includes(entry.location)&&(
            <div style={{display:"flex",alignItems:"center",gap:"3px"}}>
              <span style={{fontSize:"11px"}}>📍</span>
              <span style={{fontSize:"10px",color:"#94A3B8"}}>{entry.location}</span>
            </div>
          )}
        </div>
      )}
      <div style={{display:"flex",alignItems:"center",gap:"8px",paddingTop:"8px",borderTop:"1px solid rgba(255,255,255,0.08)"}}>
        <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6EE7B7" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span style={{color:"#6EE7B7",fontWeight:"700",fontSize:"12px"}}>{entry.rawDate}</span>
        </div>
        {entry.docType&&<span style={{marginLeft:"auto",fontSize:"10px",color:"#64748B",
          background:"rgba(255,255,255,0.06)",padding:"1px 6px",borderRadius:"4px"}}>{entry.docType}</span>}
      </div>
    </div>,
    document.body
  );
}

// ─── Mini-map navigator ───────────────────────
function TimeNavigator({ minDate, maxDate, totalMs, viewStart, viewEnd, onViewChange, entries }) {
  const trackRef   = useRef(null);
  const isDragging = useRef(false);
  const dragStart  = useRef(null);
  const dragType   = useRef(null);

  const pct = { left:((viewStart-minDate)/totalMs)*100, right:((viewEnd-minDate)/totalMs)*100 };
  const win = pct.right-pct.left;
  const now = new Date();
  const todayPct = Math.max(0,Math.min(100,((now-minDate)/totalMs)*100));
  const pctToDate = p => new Date(minDate.getTime()+(p/100)*totalMs);

  const dots = entries.filter(e=>e.eventDate).map(e=>{
    const ph=PHASE_CFG[e.phase]||PHASE_CFG["Other"];
    return { left:Math.max(0,Math.min(100,((e.eventDate-minDate)/totalMs)*100)), color:ph.bar };
  });

  const onMouseDown=(e,type)=>{
    e.preventDefault(); isDragging.current=true; dragType.current=type;
    dragStart.current={x:e.clientX,left:pct.left,right:pct.right};
    window.addEventListener("mousemove",onMove); window.addEventListener("mouseup",onUp);
  };
  const onMove=e=>{
    if(!isDragging.current||!trackRef.current) return;
    const rect=trackRef.current.getBoundingClientRect();
    const delta=((e.clientX-dragStart.current.x)/rect.width)*100;
    let{left,right}=dragStart.current; const minW=3;
    if(dragType.current==="window"){
      let nl=Math.max(0,left+delta),nr=nl+(right-left);
      if(nr>100){nr=100;nl=nr-(right-left);}
      onViewChange(pctToDate(nl),pctToDate(nr));
    } else if(dragType.current==="left"){
      onViewChange(pctToDate(Math.max(0,Math.min(right-minW,left+delta))),pctToDate(right));
    } else {
      onViewChange(pctToDate(left),pctToDate(Math.min(100,Math.max(left+minW,right+delta))));
    }
  };
  const onUp=()=>{ isDragging.current=false; window.removeEventListener("mousemove",onMove); window.removeEventListener("mouseup",onUp); };
  const onTrackClick=e=>{
    if(isDragging.current) return;
    const rect=trackRef.current?.getBoundingClientRect(); if(!rect) return;
    const p=Math.max(0,Math.min(100,((e.clientX-rect.left)/rect.width)*100));
    const span=pct.right-pct.left;
    let nl=Math.max(0,p-span/2),nr=nl+span;
    if(nr>100){nr=100;nl=nr-span;}
    onViewChange(pctToDate(nl),pctToDate(nr));
  };

  const fmtDate = d => d ? d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "";

  return (
    <div style={{padding:"6px 0 2px"}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px"}}>
        <span style={{fontSize:"10px",color:"#9CA3AF",fontWeight:"600"}}>{fmtDate(minDate)}</span>
        <span style={{fontSize:"10px",color:"#6366F1",fontWeight:"700"}}>{fmtDate(viewStart)} → {fmtDate(viewEnd)}</span>
        <span style={{fontSize:"10px",color:"#9CA3AF",fontWeight:"600"}}>{fmtDate(maxDate)}</span>
      </div>
      <div ref={trackRef} onClick={onTrackClick}
        style={{position:"relative",height:"40px",borderRadius:"8px",
          background:"#F1F5F9",border:"1px solid #E2E8F0",cursor:"pointer",overflow:"hidden"}}>
        {dots.map((d,i)=>(
          <div key={i} style={{position:"absolute",left:`${d.left}%`,top:"50%",
            transform:"translate(-50%,-50%)",width:"5px",height:"5px",
            borderRadius:"50%",background:d.color,opacity:0.6,zIndex:1}}/>
        ))}
        <div style={{position:"absolute",left:`${todayPct}%`,top:0,bottom:0,
          width:"2px",background:"#6366F1",opacity:0.7,zIndex:3}}/>
        <div style={{position:"absolute",left:0,width:`${pct.left}%`,top:0,bottom:0,background:"rgba(0,0,0,0.15)",zIndex:2}}/>
        <div style={{position:"absolute",left:`${pct.right}%`,right:0,top:0,bottom:0,background:"rgba(0,0,0,0.15)",zIndex:2}}/>
        <div onMouseDown={e=>onMouseDown(e,"window")}
          style={{position:"absolute",left:`${pct.left}%`,width:`${win}%`,top:0,bottom:0,
            border:"2px solid #6366F1",borderRadius:"6px",cursor:"grab",zIndex:4,
            background:"rgba(99,102,241,0.08)",boxSizing:"border-box"}}>
          <div onMouseDown={e=>{e.stopPropagation();onMouseDown(e,"left");}}
            style={{position:"absolute",left:"-1px",top:"50%",transform:"translateY(-50%)",
              width:"8px",height:"20px",background:"#6366F1",borderRadius:"3px 0 0 3px",
              cursor:"ew-resize",zIndex:5,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:"2px",height:"10px",background:"rgba(255,255,255,0.6)",borderRadius:"1px"}}/>
          </div>
          <div onMouseDown={e=>{e.stopPropagation();onMouseDown(e,"right");}}
            style={{position:"absolute",right:"-1px",top:"50%",transform:"translateY(-50%)",
              width:"8px",height:"20px",background:"#6366F1",borderRadius:"0 3px 3px 0",
              cursor:"ew-resize",zIndex:5,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:"2px",height:"10px",background:"rgba(255,255,255,0.6)",borderRadius:"1px"}}/>
          </div>
        </div>
      </div>
      <div style={{display:"flex",gap:"6px",marginTop:"6px",justifyContent:"center",flexWrap:"wrap"}}>
        {[
          {label:"Past 6mo",  fn:()=>{const e=new Date();const s=new Date(e);s.setMonth(s.getMonth()-6);  onViewChange(s,e);}},
          {label:"Past 1yr",  fn:()=>{const e=new Date();const s=new Date(e);s.setFullYear(s.getFullYear()-1);onViewChange(s,e);}},
          {label:"Now ±6mo",  fn:()=>{const n=new Date();const s=new Date(n);s.setMonth(s.getMonth()-6);const e=new Date(n);e.setMonth(e.getMonth()+6);onViewChange(s,e);}},
          {label:"Next 1yr",  fn:()=>{const s=new Date();const e=new Date(s);e.setFullYear(e.getFullYear()+1);onViewChange(s,e);}},
          {label:"All",       fn:()=>onViewChange(minDate,maxDate)},
        ].map(b=>(
          <button key={b.label} onClick={b.fn}
            style={{padding:"2px 10px",borderRadius:"12px",border:"1px solid #E5E7EB",
              background:"#fff",color:"#6B7280",fontSize:"10px",fontWeight:"600",cursor:"pointer"}}>
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Month/quarter header ─────────────────────
function TimelineHeader({ viewStart, viewEnd, viewMs }) {
  const months=[], now=new Date();
  const MON=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const cursor=new Date(viewStart.getFullYear(),viewStart.getMonth(),1);
  while(cursor<=viewEnd){
    const left=((cursor-viewStart)/viewMs)*100;
    const q=Math.floor(cursor.getMonth()/3)+1;
    const isNow=cursor.getFullYear()===now.getFullYear()&&Math.floor(cursor.getMonth()/3)===Math.floor(now.getMonth()/3);
    months.push({left,label:MON[cursor.getMonth()],year:cursor.getFullYear(),q,isQS:cursor.getMonth()%3===0,isNow});
    cursor.setMonth(cursor.getMonth()+1);
  }
  const qLabels=[];
  let i=0;
  while(i<months.length){
    if(months[i].isQS||i===0){
      const sl=months.slice(i,i+3);
      const mid=sl[Math.floor(sl.length/2)];
      qLabels.push({left:mid.left,label:`Q${months[i].q} ${months[i].year}`,isNow:months[i].isNow});
      i+=3;
    } else i++;
  }
  return (
    <div style={{position:"relative",height:"38px",borderBottom:"2px solid #E2E8F0",background:"#F8FAFC",overflow:"hidden"}}>
      {qLabels.map((q,i)=>(
        <div key={i} style={{position:"absolute",left:`${q.left}%`,transform:"translateX(-50%)",
          top:"2px",fontSize:"10px",fontWeight:"800",whiteSpace:"nowrap",
          color:q.isNow?"#6366F1":"#374151",background:q.isNow?"#EEF2FF":"transparent",padding:"1px 5px",borderRadius:"4px"}}>
          {q.label}
        </div>
      ))}
      {months.map((m,i)=>(
        <div key={i}>
          <div style={{position:"absolute",left:`${m.left}%`,bottom:0,height:m.isQS?"16px":"8px",width:"1px",background:m.isQS?"#94A3B8":"#E2E8F0"}}/>
          {months.length<=24&&(
            <span style={{position:"absolute",left:`${m.left+0.3}%`,bottom:"2px",
              fontSize:"8px",color:m.isNow?"#6366F1":"#94A3B8",fontWeight:m.isNow?"700":"400",whiteSpace:"nowrap"}}>
              {m.label}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Event dot row ────────────────────────────
function EventRow({ entry, viewStart, viewMs, ri, subId }) {
  const ph=PHASE_CFG[entry.phase]||PHASE_CFG["Other"];
  const fmtDate     = d => d ? d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "";
  const fmtDateShort= d => d ? d.toLocaleDateString("en-GB",{day:"2-digit",month:"short"}) : "";
  const dotLeft = entry.eventDate ? Math.max(0,Math.min(100,((entry.eventDate-viewStart)/viewMs)*100)) : null;
  const inView  = dotLeft!==null && dotLeft>=0 && dotLeft<=100;

  return (
    <div onClick={()=>openDocTab(entry.id,subId)}
      style={{display:"flex",alignItems:"stretch",borderBottom:"1px solid #F1F5F9",minHeight:"44px",
        cursor:"pointer",background:ri%2===0?"#fff":"#FAFBFF"}}>
      <div style={{width:"clamp(160px, 18vw, 240px)",flexShrink:0,padding:"7px 12px",display:"flex",alignItems:"center",gap:"8px",borderRight:"1px solid #F1F5F9"}}>
        <div style={{width:"4px",height:"28px",borderRadius:"2px",background:ph.grad,flexShrink:0}}/>
        {entry.logoUrl
          ? <img src={entry.logoUrl} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"22px",height:"22px",objectFit:"contain",borderRadius:"4px",flexShrink:0}}/>
          : <div style={{width:"22px",height:"22px",borderRadius:"5px",background:ph.light,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <span style={{fontSize:"9px",fontWeight:"800",color:ph.bar}}>{entry.sponsor?.[0]||"?"}</span>
            </div>}
        <div style={{minWidth:0}}>
          <div style={{fontSize:"11px",fontWeight:"700",color:"#1E293B",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {entry.product||entry.acronym||entry.subject.slice(0,22)}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"4px",marginTop:"1px",flexWrap:"wrap"}}>
            {entry.sponsor&&<span style={{fontSize:"9px",color:"#94A3B8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{entry.sponsor}</span>}
            {entry.countries.slice(0,2).map((c,i)=><FlagIcon key={i} country={c} size={11}/>)}
          </div>
        </div>
      </div>
      <div style={{flex:1,position:"relative",overflow:"hidden"}}>
        {(()=>{const n=new Date();const l=((n-viewStart)/viewMs)*100;
          return l>=0&&l<=100?<div style={{position:"absolute",left:`${l}%`,top:0,bottom:0,width:"2px",background:"#6366F1",opacity:0.4,zIndex:2}}/>:null;
        })()}
        {inView&&(
          <div style={{position:"absolute",left:`${dotLeft}%`,top:"50%",transform:"translate(-50%,-50%)",zIndex:3}}>
            <div style={{position:"absolute",width:"20px",height:"20px",borderRadius:"50%",
              background:`${ph.bar}22`,top:"-7px",left:"-7px",animation:"pulse 2s infinite"}}/>
            <div style={{width:"10px",height:"10px",borderRadius:"50%",background:ph.bar,
              border:"2px solid #fff",boxShadow:`0 0 0 2px ${ph.bar}44`}}/>
            {entry.timeStr&&(
              <div style={{position:"absolute",bottom:"14px",left:"50%",transform:"translateX(-50%)",
                background:ph.bar,color:"#fff",fontSize:"8px",fontWeight:"700",
                borderRadius:"4px",padding:"1px 5px",whiteSpace:"nowrap",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}>
                {entry.timeStr}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{width:"clamp(90px, 9vw, 120px)",flexShrink:0,padding:"7px 10px",borderLeft:"1px solid #F1F5F9",
        display:"flex",flexDirection:"column",justifyContent:"center"}}>
        {entry.eventDate
          ? <div style={{fontSize:"10px",color:"#374151",fontWeight:"600"}}>{fmtDateShort(entry.eventDate)}</div>
          : <div style={{fontSize:"10px",color:"#9CA3AF"}}>{entry.displayTime}</div>}
        {entry.timeStr&&<div style={{fontSize:"9px",color:"#9CA3AF",marginTop:"1px"}}>{entry.timeStr}</div>}
        {entry.docType&&<div style={{fontSize:"8px",color:"#C4B5FD",fontWeight:"600",
          background:"#F5F3FF",borderRadius:"3px",padding:"0 4px",display:"inline-block",marginTop:"1px"}}>{entry.docType}</div>}
      </div>
    </div>
  );
}

export default function EventsTimeline({ user, subproject, initialType="upcoming", hideToggle=false }) {
  const [eventtype, setEventtype] = useState(initialType);
  // Sync if parent changes initialType (e.g. Timelines switching past↔upcoming tabs)
  useEffect(() => { setEventtype(initialType); }, [initialType]);
  const [filterPhase,  setFilterPhase]  = useState(null);
  const [filterSponsor,setFilterSponsor]= useState(null);
  const [filterProduct,setFilterProduct]= useState(null);
  const [search,       setSearch]       = useState("");
  const [tip,          setTip]          = useState(null);
  const [tipPos,       setTipPos]       = useState({x:0,y:0});
  const { theme={} } = useContext(ThemeCtx)||{};
  const ctx = useContext(ThemeCtx)||{};
  const thm = ctx.theme||ctx||{};

  const { docs, loading, error } = useEventsLoader({ user, subproject, eventtype });
  const allEntries = buildEntries(docs);

  const entries = allEntries.filter(e=>{
    if(filterPhase   &&e.phase!==filterPhase)                         return false;
    if(filterSponsor &&!(e.allSponsors||[]).includes(filterSponsor))   return false;
    if(filterProduct &&e.product!==filterProduct)                      return false;
    if(search){const q=search.toLowerCase();
      if(![e.subject,e.acronym,e.product,e.sponsor,e.indication].some(v=>v?.toLowerCase().includes(q))) return false;}
    return true;
  });

  // Latest on top for both — most recent date first
  const sorted=[...entries].sort((a,b)=>
    (b.eventDate||new Date(0))-(a.eventDate||new Date(0))
  );

  const allDates=allEntries.filter(e=>e.eventDate).map(e=>e.eventDate);
  const now=new Date();
  const dataMin=allDates.length?new Date(Math.min(...allDates)):new Date(now.getFullYear(),0,1);
  const dataMax=allDates.length?new Date(Math.max(...allDates)):new Date(now.getFullYear()+1,0,1);
  const minDate=new Date(dataMin); minDate.setMonth(minDate.getMonth()-1);
  const maxDate=new Date(dataMax); maxDate.setMonth(maxDate.getMonth()+1);
  const totalMs=maxDate-minDate||1;

  const defStart=new Date(now); defStart.setMonth(defStart.getMonth()-3);
  const defEnd  =new Date(now); defEnd.setMonth(defEnd.getMonth()+9);
  const [viewStart,setViewStart]=useState(()=>new Date(Math.max(minDate,defStart)));
  const [viewEnd,  setViewEnd]  =useState(()=>new Date(Math.min(maxDate,defEnd)));

  const handleViewChange=(s,e)=>{
    setViewStart(new Date(Math.max(minDate,s)));
    setViewEnd(new Date(Math.min(maxDate,e)));
  };

  useEffect(()=>{
    if(!allDates.length) return;
    const s=new Date(now); s.setMonth(s.getMonth()-3);
    const e=new Date(now); e.setMonth(e.getMonth()+9);
    setViewStart(new Date(Math.max(minDate,s)));
    setViewEnd(new Date(Math.min(maxDate,e)));
  },[docs.length]);

  const viewMs=viewEnd-viewStart||1;

  const phases   =PHASE_ORDER.filter(ph=>allEntries.some(e=>e.phase===ph));
  const sponsors =[...new Set(allEntries.flatMap(e=>e.allSponsors).filter(Boolean))].sort();
  const products =[...new Set(allEntries.map(e=>e.product).filter(Boolean))].sort();
  const hasFilter=filterPhase||filterSponsor||filterProduct||search;

  const Spinner=()=>(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",padding:"4rem",color:"#9CA3AF"}}>
      <div style={{width:"16px",height:"16px",border:"2px solid #E5E7EB",
        borderTopColor:thm.primary||C.primary,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      Loading events...
      <style>{"@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:0.6;transform:scale(1)}50%{opacity:0.2;transform:scale(1.6)}}"}</style>
    </div>
  );

  return (
    <div style={{padding:"20px",display:"flex",flexDirection:"column",height:"100%",boxSizing:"border-box",gap:"10px"}}>

      {/* ── Header ── */}
      <div style={{display:"flex",alignItems:"center",gap:"12px",flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:"19px",fontWeight:"700",color:"#111827"}}>Events Timeline</div>
          <div style={{fontSize:"13px",color:"#9CA3AF",marginTop:"1px"}}>
            {subproject?.SUMMARY} · {sorted.length}{sorted.length!==allEntries.length?` of ${allEntries.length}`:""} events
          </div>
        </div>

        {/* Past / Upcoming toggle — hidden when embedded in Timelines */}
        {!hideToggle && (
          <div style={{display:"flex",background:"#F1F5F9",borderRadius:"10px",padding:"3px",gap:"2px"}}>
            {[{id:"upcoming",label:"📅 Upcoming"},{id:"past",label:"⏪ Past"}].map(t=>(
              <button key={t.id} onClick={()=>setEventtype(t.id)}
                style={{padding:"5px 16px",borderRadius:"8px",border:"none",cursor:"pointer",
                  fontSize:"12px",fontWeight:"600",transition:"all 0.15s",
                  background:eventtype===t.id?"#fff":"transparent",
                  color:eventtype===t.id?"#6366F1":"#64748B",
                  boxShadow:eventtype===t.id?"0 1px 4px rgba(0,0,0,0.1)":"none"}}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Phase legend */}
        <div style={{display:"flex",gap:"7px",flexWrap:"wrap",marginLeft:"auto",alignItems:"center"}}>
          {phases.map(ph=>{
            const cfg=PHASE_CFG[ph],active=filterPhase===ph;
            return(
              <button key={ph} onClick={()=>setFilterPhase(active?null:ph)}
                style={{display:"flex",alignItems:"center",gap:"5px",padding:"3px 10px",
                  borderRadius:"20px",border:`1.5px solid ${active?cfg.bar:"#E5E7EB"}`,
                  background:active?cfg.light:"#fff",cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{width:"7px",height:"7px",borderRadius:"50%",background:cfg.bar}}/>
                <span style={{fontSize:"11px",fontWeight:active?"700":"500",color:active?cfg.text:"#6B7280"}}>{ph}</span>
              </button>
            );
          })}
          <div style={{width:"1px",height:"18px",background:"#E5E7EB"}}/>
          <div style={{display:"flex",alignItems:"center",gap:"4px"}}>
            <div style={{width:"2px",height:"16px",background:"#6366F1",borderRadius:"1px"}}/>
            <span style={{fontSize:"11px",color:"#6B7280"}}>Now</span>
          </div>
        </div>
      </div>

      {error&&<div style={{padding:"10px 14px",borderRadius:"8px",background:"#FEF2F2",color:"#DC2626",fontSize:"13px"}}>{error}</div>}

      {/* ── Filters ── */}
      <div style={{display:"flex",flexDirection:"column",gap:"6px",
        background:"#F8FAFC",border:"1px solid #E5E7EB",borderRadius:"12px",padding:"10px 12px"}}>

        {/* Row 1: clear button only */}
        {hasFilter&&(
          <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
            <button onClick={()=>{setFilterPhase(null);setFilterSponsor(null);setFilterProduct(null);setSearch("");}}
              style={{padding:"4px 12px",borderRadius:"20px",border:"1px solid #FECACA",
                background:"#FEF2F2",color:"#EF4444",fontSize:"11px",fontWeight:"600",cursor:"pointer"}}>
              ✕ Clear all
            </button>
            {filterSponsor&&<span style={{fontSize:"11px",color:"#6366F1",fontWeight:"600"}}>Sponsor: {filterSponsor}</span>}
            {filterProduct&&<span style={{fontSize:"11px",color:"#0891B2",fontWeight:"600"}}>💊 {filterProduct}</span>}
          </div>
        )}

        {/* Row 2 & 3: Scrollable sponsor + product chips */}
        <ScrollChips
          label="Sponsor" items={sponsors} active={filterSponsor}
          onSelect={v=>setFilterSponsor(v)}
          activeColor="#6366F1" activeBg="#EEF2FF"
          renderLabel={s=>s}/>
        <ScrollChips
          label="Product" items={products} active={filterProduct}
          onSelect={v=>setFilterProduct(v)}
          activeColor="#0891B2" activeBg="#ECFEFF"
          renderLabel={s=>`💊 ${s}`}/>
      </div>

      {loading&&<Spinner/>}

      {!loading&&sorted.length===0&&(
        <div style={{textAlign:"center",padding:"4rem"}}>
          <div style={{fontSize:"40px",opacity:0.15,marginBottom:"1rem"}}>📅</div>
          <div style={{fontSize:"15px",color:"#9CA3AF"}}>No events match your filters</div>
        </div>
      )}

      {!loading&&sorted.length>0&&<>
        {/* ── Gantt ── */}
        <div style={{flex:1,display:"flex",flexDirection:"column",background:"#fff",
          borderRadius:"14px",border:"1px solid #E5E7EB",overflow:"hidden",
          boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>

          {/* Header */}
          <div style={{display:"flex",borderBottom:"2px solid #E2E8F0",background:"#F8FAFC",flexShrink:0}}>
            <div style={{width:"clamp(160px, 18vw, 240px)",flexShrink:0,padding:"10px 14px",
              fontSize:"10px",fontWeight:"700",color:"#9CA3AF",
              textTransform:"uppercase",letterSpacing:"0.6px",borderRight:"1px solid #E2E8F0"}}>
              Product / Sponsor
            </div>
            <div style={{flex:1}}>
              <TimelineHeader viewStart={viewStart} viewEnd={viewEnd} viewMs={viewMs}/>
            </div>
            <div style={{width:"clamp(90px, 9vw, 120px)",flexShrink:0,padding:"10px",
              fontSize:"10px",fontWeight:"700",color:"#9CA3AF",
              textTransform:"uppercase",letterSpacing:"0.6px",
              borderLeft:"1px solid #E2E8F0",textAlign:"right"}}>
              Event Date
            </div>
          </div>

          {/* Rows — mixed phases, sorted by latest */}
          <div style={{flex:1,overflowY:"auto"}}>
            {sorted.map((entry,i)=>(
              <div key={entry.id}
                onMouseEnter={e=>{setTip(entry);setTipPos({x:e.clientX,y:e.clientY});}}
                onMouseLeave={()=>setTip(null)}>
                <EventRow entry={entry} viewStart={viewStart} viewMs={viewMs} ri={i} subId={subproject?.SUBPROJECTID}/>
              </div>
            ))}
          </div>
        </div>

        {/* ── Navigator ── */}
        <div style={{background:"#fff",borderRadius:"12px",border:"1px solid #E5E7EB",
          padding:"10px 14px",boxShadow:"0 1px 6px rgba(0,0,0,0.05)"}}>
          <TimeNavigator
            minDate={minDate} maxDate={maxDate} totalMs={totalMs}
            viewStart={viewStart} viewEnd={viewEnd}
            onViewChange={handleViewChange}
            entries={sorted}/>
        </div>
      </>}

      {tip&&<Tooltip entry={tip} x={tipPos.x} y={tipPos.y}/>}
    </div>
  );
}